export const BOOKING_ANALYTICS_EVENT_NAMES = [
  "booking_started",
  "service_selected",
  "slot_viewed",
  "slot_conflict",
  "waitlist_opened",
  "client_selected",
  "booking_confirmed",
  "booking_cancelled"
] as const;

export type BookingAnalyticsEventName = (typeof BOOKING_ANALYTICS_EVENT_NAMES)[number];

export type BookingAnalyticsActor = "anonymous" | "customer";
export type BookingAnalyticsStep = "datetime" | "recurrence" | "information" | "payment" | "confirmation";
export type BookingAnalyticsKind = "single" | "recurring";
export type BookingAnalyticsReason =
  | "availability_conflict"
  | "hold_expired"
  | "payment_failed"
  | "payment_expired"
  | "user_cancelled"
  | "request_failed";

/**
 * Privacy-safe browser event emitted by booking clients. It deliberately
 * excludes names, email addresses, phone numbers, notes and booking ids.
 */
export interface BookingAnalyticsEvent {
  version: 1;
  name: BookingAnalyticsEventName;
  flowId: string;
  timestamp: string;
  actor: BookingAnalyticsActor;
  step: BookingAnalyticsStep;
  attempt: number;
  serviceId?: string;
  date?: string;
  slotCount?: number;
  kind?: BookingAnalyticsKind;
  durationMs?: number;
  reason?: BookingAnalyticsReason;
}

export type BookingAnalyticsEventInput = Omit<BookingAnalyticsEvent, "version" | "timestamp" | "attempt"> & {
  timestamp?: string;
  attempt?: number;
};

export function createBookingAnalyticsEvent(input: BookingAnalyticsEventInput): BookingAnalyticsEvent {
  const attempt = Number.isFinite(input.attempt) ? Math.max(1, Math.trunc(input.attempt ?? 1)) : 1;
  const slotCount = input.slotCount === undefined ? undefined : Math.max(0, Math.trunc(input.slotCount));
  const durationMs = input.durationMs === undefined ? undefined : Math.max(0, Math.trunc(input.durationMs));
  return {
    version: 1,
    name: input.name,
    flowId: input.flowId,
    timestamp: input.timestamp ?? new Date().toISOString(),
    actor: input.actor,
    step: input.step,
    attempt,
    ...(input.serviceId ? { serviceId: input.serviceId } : {}),
    ...(input.date ? { date: input.date } : {}),
    ...(slotCount === undefined ? {} : { slotCount }),
    ...(input.kind ? { kind: input.kind } : {}),
    ...(durationMs === undefined ? {} : { durationMs }),
    ...(input.reason ? { reason: input.reason } : {})
  };
}
